import { SessionTimeoutDialog } from 'app/modules/shared/SessionTimeoutDialog/SessionTimeoutDialog';
import React, { createContext, ReactNode, useContext } from 'react';

import { useAutoLogout } from '../hooks/useAutoLogout';

interface AutoLogoutContextType {
  isWarning: boolean
  remainingTime: number
  extendSession: () => void
  forceLogout: () => void
  pauseTimer: () => void
  resumeTimer: () => void
}

const AutoLogoutContext = createContext<AutoLogoutContextType | undefined>(undefined);

interface AutoLogoutProviderProps {
  children: ReactNode
  timeoutDuration?: number
  warningDuration?: number
  enabled?: boolean
}

export const AutoLogoutProvider: React.FC<AutoLogoutProviderProps> = ({
  children,
  timeoutDuration = 20 * 60 * 1000, // 20 minutes
  warningDuration = 2 * 60 * 1000, // 2 minutes
  enabled = true
}) => {
  const autoLogout = useAutoLogout({
    timeoutDuration,
    warningDuration,
    enabled,
    onWarning: () => {
      console.log('Session timeout warning displayed');
    },
    onTimeout: () => {
      console.log('User logged out due to inactivity');
    },
    onUserActive: () => {
      console.log('User became active again');
    }
  });

  return (
    <AutoLogoutContext.Provider value={autoLogout}>
      {children}
      <SessionTimeoutDialog
        show={autoLogout.isWarning}
        remainingTime={autoLogout.remainingTime}
        totalWarningTime={warningDuration}
        onContinue={autoLogout.extendSession}
        onLogout={autoLogout.forceLogout}
        title="Session Expiring Soon"
        message="Your session will expire due to inactivity. Do you want to continue working?"
        continueButtonText="Stay Logged In"
        logoutButtonText="Logout"
      />
    </AutoLogoutContext.Provider>
  );
};

export const useAutoLogoutContext = (): AutoLogoutContextType => {
  const context = useContext(AutoLogoutContext);
  if (!context) {
    throw new Error('useAutoLogoutContext must be used within an AutoLogoutProvider');
  }
  return context;
};
